import { useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { useDidUpdate } from '@toss/react';
import { useDelayedLoading } from '@/hooks';
import { cn } from '@/utils';
type InfiniteListViewProps<T> = {
  items: T[] | undefined;
  isLoading: boolean;
  isError: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  isFetchingNextPage?: boolean;
  renderItem: (item: T, index: number) => React.ReactNode;
  EmptyComponent?: React.ReactNode;
  ErrorComponent?: React.ReactNode;
  LoadingComponent?: React.ReactNode;
  HeaderComponent?: React.ReactNode;
  className?: string;
};
const DEFAULT_LOADING_COMPONENT = <>Loading...</>;
export function InfiniteListView<T>({
  items,
  isLoading,
  isError,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage = false,
  renderItem,
  EmptyComponent = null,
  ErrorComponent = null,
  LoadingComponent = DEFAULT_LOADING_COMPONENT,
  HeaderComponent = null,
  className = ''
}: InfiniteListViewProps<T>) {
  const {
    ref,
    inView
  } = useInView({
    triggerOnce: false,
    threshold: 0.1
  });
  const delayedLoading = useDelayedLoading(isLoading, 2000);
  const isFetchingNextPageRef = useRef(isFetchingNextPage);
  useEffect(() => {
    isFetchingNextPageRef.current = isFetchingNextPage;
  }, [isFetchingNextPage]);
  useDidUpdate(() => {
    if (inView && hasNextPage && !isFetchingNextPageRef.current) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, fetchNextPage]);
  if (isLoading || !items) return null;
  if (delayedLoading) return <>{LoadingComponent}</>;
  if (isError) return <>{ErrorComponent}</>;
  if (items.length === 0 && !hasNextPage) return <>{EmptyComponent}</>;
  return <div className={cn(`grid max-tablet-sm:grid-cols-1 grid-cols-[repeat(auto-fit,minmax(595px,1fr))] gap-x-xs pb-lg`, className)} data-sentry-component="InfiniteListView" data-sentry-source-file="InfiniteListView.tsx">
      {HeaderComponent}
      {items.map((item, idx) => renderItem(item, idx))}
      <div key="in-view-trigger" ref={ref} />
    </div>;
}